// Contact.jsx - Contact page for CodeRated
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  MessageSquare, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  Eye,
  CheckCircle,
  Zap,
  Users,
  Building,
  HelpCircle,
  Bug,
  Star,
  ArrowRight
} from 'lucide-react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry', icon: MessageSquare, color: 'blue' },
    { value: 'sales', label: 'Sales & Pricing', icon: Building, color: 'green' },
    { value: 'support', label: 'Technical Support', icon: HelpCircle, color: 'purple' },
    { value: 'partnership', label: 'Partnership', icon: Users, color: 'orange' },
    { value: 'bug', label: 'Bug Report', icon: Bug, color: 'red' },
    { value: 'feedback', label: 'Feature Feedback', icon: Star, color: 'yellow' }
  ];

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      description: 'Send us an email anytime',
      detail: 'hello@coderated.com',
      response: 'Usually within 4 hours'
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      description: 'Chat with our team',
      detail: 'Available in app',
      response: 'Instant response'
    },
    {
      icon: Phone,
      title: 'Enterprise Sales',
      description: 'Talk to our sales team',
      detail: '+1 (555) 123-4567',
      response: 'Schedule a call'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full text-center">
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-12">
            <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="text-4xl font-bold text-white mb-6">Message Sent!</h1>
            <p className="text-xl text-slate-300 mb-8">
              Thank you for reaching out. We've received your message and will get back to you within 4 hours.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
              >
                Back to Home
              </Link>
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    company: '',
                    subject: '',
                    message: '',
                    inquiryType: 'general'
                  });
                }}
                className="border border-slate-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-800/50 transition-all"
              >
                Send Another Message
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      {/* Navigation */}
      <nav className="relative z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="group flex items-center gap-4 p-2 hover:scale-110 transition-all duration-300">
              <div className="relative">
                <div className="w-12 h-10 bg-gradient-to-b from-gray-300 to-gray-500 rounded-lg border-2 border-gray-600 shadow-xl">
                  <div className="w-8 h-5 bg-black mx-auto mt-1 rounded border border-gray-800 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex justify-center mt-0.5 gap-1">
                    <div className="w-1 h-0.5 bg-red-500 rounded"></div>
                    <div className="w-1 h-0.5 bg-green-500 rounded"></div>
                  </div>
                </div>
              </div>
              <span className="text-2xl font-bold text-white">CodeRated</span>
            </Link>
            
            <Link to="/" className="text-slate-300 hover:text-white transition-colors">
              ← Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Hero Section */}
          <section className="text-center mb-16">
            <h1 className="text-6xl md:text-7xl font-black mb-8">
              <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                Get in Touch
              </span>
            </h1>
            
            <p className="text-2xl md:text-3xl text-slate-300 mb-6 font-light max-w-4xl mx-auto">
              Have questions? We'd love to hear from you. 
              <span className="text-blue-400 font-semibold"> Send us a message</span> and we'll respond as soon as possible.
            </p>
          </section>

          <div className="grid lg:grid-cols-3 gap-12">
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8">
                <h2 className="text-3xl font-bold text-white mb-8">Send us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Inquiry Type */}
                  <div>
                    <label className="block text-white font-semibold mb-4">What can we help you with?</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {inquiryTypes.map((type) => {
                        const IconComponent = type.icon;
                        return (
                          <label
                            key={type.value}
                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${
                              formData.inquiryType === type.value
                                ? `border-${type.color}-500 bg-${type.color}-500/10`
                                : 'border-slate-700 hover:border-slate-600'
                            }`}
                          >
                            <input
                              type="radio"
                              name="inquiryType"
                              value={type.value}
                              checked={formData.inquiryType === type.value}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            <div className="text-center">
                              <IconComponent className={`w-6 h-6 mx-auto mb-2 ${
                                formData.inquiryType === type.value 
                                  ? `text-${type.color}-400` 
                                  : 'text-slate-400'
                              }`} />
                              <span className={`text-sm font-medium ${
                                formData.inquiryType === type.value 
                                  ? 'text-white' 
                                  : 'text-slate-400'
                              }`}>
                                {type.label}
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Personal Info */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-semibold mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white font-semibold mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-semibold mb-2">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                        placeholder="Your company name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white font-semibold mb-2">Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                        placeholder="Brief subject line"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-white font-semibold mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-8">
              
              {/* Contact Methods */}
              <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-6">Other Ways to Reach Us</h3>
                
                <div className="space-y-6">
                  {contactMethods.map((method, index) => {
                    const IconComponent = method.icon;
                    return (
                      <div key={index} className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shrink-0">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white">{method.title}</h4>
                          <p className="text-slate-400 text-sm mb-1">{method.description}</p>
                          <p className="text-blue-400 font-medium">{method.detail}</p>
                          <p className="text-slate-500 text-xs">{method.response}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Office Info */}
              <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-6">Our Office</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-400 mt-1 shrink-0" />
                    <div>
                      <p className="text-white font-medium">San Francisco, CA</p>
                      <p className="text-slate-400 text-sm">123 AI Innovation Drive<br/>San Francisco, CA 94105</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-green-400 mt-1 shrink-0" />
                    <div>
                      <p className="text-white font-medium">Business Hours</p>
                      <p className="text-slate-400 text-sm">Monday - Friday: 9:00 AM - 6:00 PM PST<br/>Weekend: Limited support</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Need Help Right Away?</h3>
                <div className="space-y-3">
                  <Link 
                    to="/help" 
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-all group"
                  >
                    <span className="text-white">Help Center & FAQ</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                  </Link>
                  <Link 
                    to="/status" 
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-all group"
                  >
                    <span className="text-white">System Status</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                  </Link>
                  <Link 
                    to="/search" 
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-all group"
                  >
                    <span className="text-white">Try Our Demo</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}